import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Chip,
    useMediaQuery,
    useTheme,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { ArrowBack, Add, Delete, Edit } from '@mui/icons-material';
import api from '../../api/axios';

const SubscriptionDetail = () => {
    const { tenantId, subscriptionId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [addonDialogOpen, setAddonDialogOpen] = useState(false);
    const [editingAddon, setEditingAddon] = useState<any>(null);
    const [addonForm, setAddonForm] = useState({ addon_id: '', quantity: 1 });
    const [error, setError] = useState('');

    // Fetch subscription details with addons
    const { data: subscriptionData, isLoading } = useQuery({
        queryKey: ['subscription-detail', tenantId, subscriptionId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}/subscriptions/${subscriptionId}`);
            return res.data?.data;
        },
    });

    // Fetch subscription addons
    const { data: addonsData, isLoading: addonsLoading } = useQuery({
        queryKey: ['subscription-addons', tenantId, subscriptionId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}/subscriptions/${subscriptionId}/addons`);
            return res.data;
        },
    });

    // Fetch available addons
    const { data: availableAddons } = useQuery({
        queryKey: ['available-addons'],
        queryFn: async () => {
            const res = await api.get('/superadmin/addons', { params: { per_page: 100 } });
            return res.data?.data?.data?.filter((a: any) => a.is_active) || [];
        },
    });

    // Add addon mutation
    const addAddonMutation = useMutation({
        mutationFn: async (data: any) => {
            return api.post(`/superadmin/tenants/${tenantId}/subscriptions/${subscriptionId}/addons`, data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['subscription-addons', tenantId, subscriptionId] });
            setAddonDialogOpen(false);
            setAddonForm({ addon_id: '', quantity: 1 });
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    // Remove addon mutation
    const removeAddonMutation = useMutation({
        mutationFn: async (addonId: number) => {
            return api.delete(`/superadmin/tenants/${tenantId}/subscriptions/${subscriptionId}/addons/${addonId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscription-addons', tenantId, subscriptionId] });
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    // Update addon mutation
    const updateAddonMutation = useMutation({
        mutationFn: async ({ addonId, quantity }: { addonId: number; quantity: number }) => {
            return api.put(`/superadmin/tenants/${tenantId}/subscriptions/${subscriptionId}/addons/${addonId}`, { quantity });
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['subscription-addons', tenantId, subscriptionId] });
            closeAddonDialog();
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    const openAddonDialog = (addon?: any) => {
        if (addon) {
            setEditingAddon(addon);
            setAddonForm({ addon_id: addon.id.toString(), quantity: addon.pivot?.quantity || 1 });
        } else {
            setEditingAddon(null);
            setAddonForm({ addon_id: '', quantity: 1 });
        }
        setError('');
        setAddonDialogOpen(true);
    };

    const closeAddonDialog = () => {
        setAddonDialogOpen(false);
        setEditingAddon(null);
        setAddonForm({ addon_id: '', quantity: 1 });
        setError('');
    };

    const handleAddonSubmit = () => {
        if (editingAddon) {
            updateAddonMutation.mutate({ addonId: editingAddon.id, quantity: addonForm.quantity });
        } else {
            addAddonMutation.mutate(addonForm);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const attachedAddons = addonsData?.data || [];
    const effectiveLimits = addonsData?.effective_limits || {};

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <IconButton onClick={() => navigate(`/super-admin/tenants/${tenantId}`)}>
                    <ArrowBack />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Subscription #{subscriptionId}</Typography>
                    <Typography color="text.secondary">
                        {subscriptionData?.plan?.name || 'Unknown Plan'} • ₹{subscriptionData?.amount_paid || 0}
                    </Typography>
                </Box>
                <Box>
                    <Chip
                        label={subscriptionData?.is_active ? 'Active' : 'Inactive'}
                        color={subscriptionData?.is_active ? 'success' : 'default'}
                    />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Subscription Info */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Subscription Details</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                        <Box>
                            <Typography color="text.secondary" variant="body2">Plan</Typography>
                            <Typography>{subscriptionData?.plan?.name}</Typography>
                        </Box>
                        <Box>
                            <Typography color="text.secondary" variant="body2">Amount Paid</Typography>
                            <Typography>₹{subscriptionData?.amount_paid}</Typography>
                        </Box>
                        <Box>
                            <Typography color="text.secondary" variant="body2">Expires</Typography>
                            <Typography>
                                {subscriptionData?.expires_at
                                    ? new Date(subscriptionData.expires_at).toLocaleDateString()
                                    : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Effective Limits (Base + Addons)</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Citizens: ${effectiveLimits.citizens || 'N/A'}`} variant="outlined" />
                        <Chip label={`Storage: ${effectiveLimits.storage || 'N/A'} MB`} variant="outlined" />
                    </Box>
                </CardContent>
            </Card>

            {/* Addons */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Attached Addons</Typography>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => openAddonDialog()}
                        >
                            Add Addon
                        </Button>
                    </Box>

                    {addonsLoading ? (
                        <CircularProgress size={24} />
                    ) : attachedAddons.length === 0 ? (
                        <Typography color="text.secondary">No addons attached to this subscription.</Typography>
                    ) : (
                        <TableContainer>
                            <Table size={isMobile ? 'small' : 'medium'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Addon</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Qty</TableCell>
                                        <TableCell>Total Price</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {attachedAddons.map((addon: any) => (
                                        <TableRow key={addon.id}>
                                            <TableCell>{addon.name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={addon.type}
                                                    size="small"
                                                    color={addon.type === 'citizens' ? 'primary' : 'secondary'}
                                                />
                                            </TableCell>
                                            <TableCell>{addon.pivot?.quantity || 1}</TableCell>
                                            <TableCell>₹{addon.pivot?.total_price || addon.price}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openAddonDialog(addon)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        if (window.confirm('Remove this addon?')) {
                                                            removeAddonMutation.mutate(addon.id);
                                                        }
                                                    }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Add/Edit Addon Dialog */}
            <Dialog open={addonDialogOpen} onClose={closeAddonDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>{editingAddon ? 'Edit Addon Quantity' : 'Add Addon to Subscription'}</DialogTitle>
                <form onSubmit={(e) => { e.preventDefault(); handleAddonSubmit(); }}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        {!editingAddon && (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Addon</InputLabel>
                                <Select
                                    value={addonForm.addon_id}
                                    label="Addon"
                                    required
                                    onChange={(e) => setAddonForm({ ...addonForm, addon_id: e.target.value })}
                                >
                                    {availableAddons?.map((addon: any) => (
                                        <MenuItem key={addon.id} value={addon.id}>
                                            {addon.name} ({addon.type}) - ₹{addon.price}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {editingAddon && (
                            <Typography sx={{ mb: 2 }}>Editing: <strong>{editingAddon.name}</strong></Typography>
                        )}

                        <TextField
                            label="Quantity"
                            type="number"
                            fullWidth
                            required
                            value={addonForm.quantity}
                            onChange={(e) => setAddonForm({ ...addonForm, quantity: parseInt(e.target.value) || 1 })}
                            inputProps={{ min: 1 }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={closeAddonDialog}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={addAddonMutation.isPending || updateAddonMutation.isPending}>
                            {(addAddonMutation.isPending || updateAddonMutation.isPending) ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default SubscriptionDetail;
